import Button from '../../components/form-control/Button';
import { Formik } from 'formik';
import Input from '../../components/form-control/Input';
import * as Yup from 'yup';
import { useState } from 'react';
import { editTeam } from '../../api/requests';
import SelectDropdown, { ISelectDropdownItem } from '../../components/form-control/SelectDropdown';
import { CheckBoxInput } from '../../components/form-control/CheckBoxInput';
import { GHANA_REGIONS } from '../../resources/constants';
import usePartner from '../../hooks/partnerHook';
import { useQuery } from '@tanstack/react-query';
import { getFormattedPhoneNumber, getPhoneNumberFromShortCodeFormat, removeUnderscoresAndCapitalize, showToast } from '../../utils/miscFunctions';
import IntlPhoneInput from '../../components/form-control/IntlPhoneInput/IntlPhoneInput';
import { useSelector } from 'react-redux';
import { getEnumsFromState } from '../../states/slices/resourceSlice';
import { IEnum } from '../../resources/interfaces';
import useUserAccount from '../../hooks/userAccountHook';

export interface EditTeamProps {
  team: any;
  onProceed: (data?: any) => void;
  onClose: () => void;
}

const EditTeam = ({ onProceed, onClose, team }: EditTeamProps) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { getAllSlackWorkspaces } = usePartner();
  const enums = useSelector(getEnumsFromState)
  const companyTypes: IEnum[] = enums?.companyTypes
  const { data: workspaces } = useQuery({ queryKey: ['workspaces'], queryFn: getAllSlackWorkspaces });
  const { user } = useUserAccount();

  const submitHandler = (values: any) => {
    setSubmitting(true);
    const payload = {
      ...values,
      name: values.name,
      type: values.type,
      email: values.email,
      mobilePhone: getFormattedPhoneNumber(values.mobilePhone),
      slackWorkspaceId: values.slackWorkspaceId,
      addMembers: values.addMembers,
      region: values.region,
      city: values.city
    };

    const teamID = team.id

    editTeam(teamID, payload)
      .then((response: any) => {
        showToast.success(response?.message ?? 'Company details updated successfully');
        onProceed();
      })
      .catch((error: any) => {
        showToast.error(error?.description);
      })
      .finally(() => setSubmitting(false));
  };


  return (
    <div className="bg-white p-[1rem] w-full rounded-xl dark:bg-darkBg">
      <Formik
        initialValues={{
          name: team.name,
          type: team.type,
          region: team.region,
          city: team.city,
          email: team.email,
          mobilePhone: getPhoneNumberFromShortCodeFormat(team.mobilePhone),
          slackWorkspaceId: team.slackWorkspaceId,
          addMembers: team.addMembers,
        }}
        validateOnBlur
        validationSchema={Yup.object({
          name: Yup.string().required('Team name is requied.'),
          type: Yup.string().required('Please select team type')
        })}
        onSubmit={(values) => {
          submitHandler(values);
        }}>
        {({ values, errors, handleSubmit, handleChange, setFieldValue, handleBlur, touched }) => (
          <form className="space-y-4" onSubmit={handleSubmit}>

            <div className="space-y-5">
              <Input
                label="Company Name"
                placeholder="Enter company name"
                name="name"
                value={values.name}
                type="text"
                wrapperClasses=""
                required
                onChange={handleChange}
                onBlur={handleChange}
                error={touched.name ? errors.name : undefined}
                helperText={touched.name ? errors.name : undefined}
              />

              <SelectDropdown
                label="Select Company Type"
                name="type"
                list={companyTypes?.map((type: IEnum) => ({
                  name: removeUnderscoresAndCapitalize(type.title),
                  value: type.key,
                  description: type.description,
                }))}
                listSelectedValue={values.type}
                onChange={(val: ISelectDropdownItem | null) => {
                  setFieldValue('type', val);
                }}
                onBlur={handleBlur}
                placeholder="Select company type"
                required
              />

              <SelectDropdown
                label="Region"
                name="region"
                listSelectedValue={values.region}
                list={GHANA_REGIONS?.map((region: string, id: number) => ({
                  name: region,
                  value: region
                }))}
                onChange={(val: string | null) => setFieldValue('region', val)}
                onBlur={handleBlur}
                placeholder="Select company region"
                required
                error={touched.region ? errors.region : undefined}
                helperText={touched.region ? errors.region : undefined}
              />

              {
                values.region &&
                <Input
                  label="City"
                  placeholder="Enter city"
                  name="city"
                  value={values.city}
                  type="text"
                  wrapperClasses=""
                  required
                  onChange={handleChange}
                  onBlur={handleChange}
                  error={touched.city ? errors.city : undefined}
                  helperText={touched.city ? errors.city : undefined}
                />
              }

              {user?.isInternalOwner() && (
                <SelectDropdown
                  name="slackWorkspaceId"
                  label="Slack Workspace"
                  placeholder="Select workspace "
                  listSelectedValue={values.slackWorkspaceId}
                  wrapperClasses=""
                  onChange={(value: any) => {
                    setFieldValue('slackWorkspaceId', value);
                  }}
                  list={workspaces?.map((workspace: any) => ({
                    name: workspace?.name,
                    value: workspace?.id
                  }))}
                  error={touched.slackWorkspaceId ? errors.slackWorkspaceId : undefined}
                  helperText={touched.slackWorkspaceId ? errors.slackWorkspaceId : undefined}
                />
              )}

              {values.slackWorkspaceId &&
                <CheckBoxInput
                  label={'Add members to workspace'}
                  name={'addMembers'}
                  checked={values?.addMembers}
                  onChange={(value: boolean) => {
                    setFieldValue("addMembers", value)
                  }}
                />
              }

              <IntlPhoneInput
                label="Phone Number"
                placeholder="Enter phone number"
                value={values.mobilePhone}
                onChange={(value: string | undefined) => {
                  setFieldValue('mobilePhone', value);
                }}
                onBlur={handleChange}
                error={touched.mobilePhone ? errors.mobilePhone : undefined}
                helperText={touched.mobilePhone ? errors.mobilePhone : undefined}
              />

              <Input
                label="Email"
                placeholder="Enter enter"
                name="email"
                value={values.email}
                type="text"
                wrapperClasses=""
                required
                onChange={handleChange}
                onBlur={handleChange}
                error={touched.email ? errors.email : undefined}
                helperText={touched.email ? errors.email : undefined}
              />
            </div>

            <div className="w-full flex gap-4 !mb-4">
              <Button
                type="button"
                label="Cancel"
                onClick={onClose}
                bgColor='secondary'
                  fullWidth
                loading={submitting}
                disabled={submitting}
              />
              <Button
                type="submit"
                label="Submit"
                bgColor='primary'
                fullWidth
                loading={submitting}
                disabled={submitting}
              />
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default EditTeam;
